import * as React from "react";
import * as uuid from "uuid";

import { Drawer } from "../../drawer";
import { useScrollToTop } from "../../hooks";

import {
  JoinDrawerUserExperience_Step,
  OneTimePasswordUserExperience_Step,
  useJoinDrawer,
} from "../../providers";

import {
  JoinDrawerFragmentProps,
  useJoinDrawerFragment,
} from "../join-drawer-fragment";

interface RightDrawerConsumerInterface {
  handleBack: JoinDrawerFragmentProps["handleBack"];
  handleClose: JoinDrawerFragmentProps["handleClose"];
}
const defaultValue = {} as unknown as RightDrawerConsumerInterface;
const RightDrawerContext = React.createContext(defaultValue);

export interface RightDrawerProps {
  present?: boolean;
  drawerClassName?: string;
  handleBack: JoinDrawerFragmentProps["handleBack"];
  handleClose: JoinDrawerFragmentProps["handleClose"];
}
export const RightDrawer: React.FunctionComponent<RightDrawerProps> = (
  props
) => {
  const {
    presentingUserExperienceStep: joinDrawer_presentingUserExperienceStep,
    showBackButton,
    handleBack: joinDrawer_handleBack,
    handleClose: joinDrawer_handleClose,
  } = useJoinDrawer();

  const { classNameForTheDrawer: joinDrawer_classNameForTheDrawer } =
    useJoinDrawerFragment(joinDrawer_presentingUserExperienceStep);

  useScrollToTop({
    selector: `aside.${joinDrawer_classNameForTheDrawer.split(" ").join(".")}`,
  });

  const drawerGuid = React.useMemo(() => {
    return uuid.v4();
  }, []);

  const handleClose = React.useCallback(() => {
    props.handleClose?.();
    joinDrawer_handleClose();
  }, [props.handleClose, joinDrawer_handleClose]);

  const handleBack = React.useCallback(() => {
    props.handleBack?.();
    joinDrawer_handleBack();
  }, [props.handleBack, joinDrawer_handleBack]);

  const present = React.useMemo(() => {
    return (
      !!props.present ||
      joinDrawer_presentingUserExperienceStep !==
        JoinDrawerUserExperience_Step.NOT_PRESENTING ||
      joinDrawer_presentingUserExperienceStep !==
        OneTimePasswordUserExperience_Step.NOT_PRESENTING
    );
  }, [props.present, joinDrawer_presentingUserExperienceStep]);

  const drawerClassName = React.useMemo(() => {
    return `${joinDrawer_classNameForTheDrawer} ${props.drawerClassName || ""}`;
  }, [joinDrawer_classNameForTheDrawer, props.drawerClassName]);

  const consumerValue: RightDrawerConsumerInterface = React.useMemo(
    () => ({
      handleBack,
      handleClose,
    }),
    [handleBack, handleClose]
  );

  return (
    <RightDrawerContext.Provider value={consumerValue}>
      <Drawer
        guid={drawerGuid}
        present={present}
        className={drawerClassName}
        handleBack={handleBack}
        showBackButton={showBackButton}
        handleClose={handleClose}
      >
        {props.children || []}
      </Drawer>
    </RightDrawerContext.Provider>
  );
};

export const useRightDrawerLocalUiContext = () => {
  const context = React.useContext(RightDrawerContext);
  if (context === defaultValue) {
    throw new Error(
      "useRightDrawerLocalUiContext must be used within a RightDrawer"
    );
  }
  return context;
};
